/**
 * UserDrawer.tsx
 *
 * File-level:
 * - Drawer component that shows a compact user profile and actions when a user is selected.
 * - Fetches user details via api/admin.fetchUserDetails if available, otherwise shows safe fallback data.
 *
 * Notes:
 * - Focused responsibility: display user details, documents, and quick moderation actions.
 * - Keeps API usage optional so missing backend module won't break the admin UI.
 */

import React, { useEffect, useState } from 'react';
import { X, CheckCircle } from 'lucide-react';

export interface UserDrawerProps {
  /** Selected user id to display (null hides drawer) */
  userId: string | null;
  /** Called when drawer should be closed */
  onClose: () => void;
}

/**
 * UserDetails
 *
 * @interface Basic user details used inside the drawer
 */
interface UserDetails {
  userId: string;
  name?: string;
  age?: number | string;
  city?: string;
  state?: string;
  lastLogin?: string;
  verified?: boolean;
  documents?: Array<{ id: string; type?: string; uploadedAt?: string }>;
  activity?: string[];
}

/**
 * UserDrawer
 *
 * @description Drawer that displays user summary, documents and activity.
 * Attempts to fetch details from api/admin.fetchUserDetails if available; falls back to mock data.
 */
export function UserDrawer({ userId, onClose }: UserDrawerProps) {
  const [user, setUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!userId) {
        setUser(null);
        return;
      }
      setLoading(true);
      try {
        let fetched: any = null;
        try {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const adminApi = require('../../api/admin');
          if (adminApi && typeof adminApi.fetchUserDetails === 'function') {
            fetched = await adminApi.fetchUserDetails(userId);
          }
        } catch (err) {
          // api/admin not available; use fallback
          // eslint-disable-next-line no-console
          console.warn('[UserDrawer] api/admin.fetchUserDetails not available, using mock data');
        }

        if (!mounted) return;

        if (fetched) {
          setUser(fetched);
        } else {
          // Fallback mock user
          setUser({
            userId,
            name: 'Sample User',
            age: 30,
            city: 'Unknown',
            state: 'Unknown',
            lastLogin: '—',
            verified: false,
            documents: [{ id: 'd1', type: 'Aadhaar - Front', uploadedAt: '2025-01-01' }],
            activity: ['Profile created', 'Photo uploaded'],
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[UserDrawer] failed to load user', err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId]);

  if (!userId) return null;

  /**
   * handleSuspend
   *
   * @description Placeholder suspend action. Calls api/admin.adminActionSuspend if available.
   */
  async function handleSuspend() {
    try {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const adminApi = require('../../api/admin');
        if (adminApi && typeof adminApi.adminActionSuspend === 'function') {
          await adminApi.adminActionSuspend(userId, { reason: 'manual-suspend' });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[UserDrawer] suspend API not available, using placeholder');
      }
      // eslint-disable-next-line no-alert
      alert('User suspended (placeholder)');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[UserDrawer] suspend failed', err);
      // eslint-disable-next-line no-alert
      alert('Failed to suspend user (placeholder)');
    }
  }

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white border-l shadow-lg z-40">
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <div className="text-sm text-slate-500">User</div>
          <div className="text-lg font-semibold">{user?.name ?? userId}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              handleSuspend();
            }}
            className="px-3 py-1 bg-rose-50 text-rose-700 rounded"
          >
            Suspend
          </button>
          <button onClick={() => onClose()} className="p-2 rounded hover:bg-slate-50" aria-label="Close drawer">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto h-[calc(100%-64px)]">
        {loading && <div className="text-sm text-slate-500">Loading...</div>}

        {!loading && user && (
          <>
            <section>
              <h4 className="text-sm font-medium">Summary</h4>
              <div className="text-sm text-slate-600 mt-2 space-y-1">
                <div>Age: {user.age ?? '—'}</div>
                <div>Location: {user.city ?? '—'}, {user.state ?? '—'}</div>
                <div>Last login: {user.lastLogin ?? '—'}</div>
                <div>
                  Verification: {user.verified ? <span className="text-green-600">Verified</span> : <span className="text-amber-500">Pending</span>}
                </div>
              </div>
            </section>

            <section>
              <h4 className="text-sm font-medium">Documents</h4>
              <div className="mt-2 space-y-2">
                {(user.documents || []).map((d) => (
                  <div key={d.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <div className="font-medium text-sm">{d.type}</div>
                      <div className="text-xs text-slate-500">{d.uploadedAt}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="px-2 py-1 bg-green-50 text-green-700 rounded inline-flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 mr-1" />Approve
                      </button>
                      <button className="px-2 py-1 bg-rose-50 text-rose-700 rounded text-sm">Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h4 className="text-sm font-medium">Activity Timeline</h4>
              <ul className="mt-2 text-xs text-slate-500 space-y-2">
                {(user.activity || []).map((a, idx) => (
                  <li key={idx}>• {a}</li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
}